import {
	ArrowNarrowDown,
	ArrowNarrowUp,
	ChevronSelectorVertical,
	DotsGrid,
	HelpCircle,
} from "@untitledui/icons";
import type { HTMLAttributes, ReactNode } from "react";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

export const TableHeaderCellAlign = {
	Left: "left",
	Center: "center",
	Right: "right",
} as const;

export type TableHeaderCellAlign =
	(typeof TableHeaderCellAlign)[keyof typeof TableHeaderCellAlign];

export const TableHeaderCellSortable = {
	None: "none",
	Unsorted: "unsorted",
	Asc: "asc",
	Desc: "desc",
} as const;

export type TableHeaderCellSortable =
	(typeof TableHeaderCellSortable)[keyof typeof TableHeaderCellSortable];

export const TableHeaderCellState = {
	Default: "default",
	Hover: "hover",
	Focused: "focused",
} as const;

export type TableHeaderCellState =
	(typeof TableHeaderCellState)[keyof typeof TableHeaderCellState];

/**
 * Map TanStack column sort state to Destiny {@link TableHeaderCellSortable}.
 * Used by the live Aria shell (`CustomTable.Head`) so sort icons stay in one place.
 */
export function tableHeaderCellSortableFromColumn(options: {
	canSort: boolean;
	isSorted: false | "asc" | "desc";
}): TableHeaderCellSortable {
	if (!options.canSort) return TableHeaderCellSortable.None;
	if (options.isSorted === "asc") return TableHeaderCellSortable.Asc;
	if (options.isSorted === "desc") return TableHeaderCellSortable.Desc;
	return TableHeaderCellSortable.Unsorted;
}

export interface TableHeaderCellProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "content"> {
	/** Horizontal alignment (Figma `align`). */
	align?: TableHeaderCellAlign;
	/** Sort affordance (Figma `sortable`). */
	sortable?: TableHeaderCellSortable;
	/**
	 * Interaction state for design-system previews (Figma `state`).
	 * In live tables, hover/focus usually come from the Aria column.
	 */
	state?: TableHeaderCellState;
	/** Column title (Figma `Label`). */
	label?: string;
	/** Secondary categorization above the label (Figma `Overline`). */
	overline?: string;
	/** Show the overline when provided (Figma `hasOverline`). */
	hasOverline?: boolean;
	/** Show the help/tooltip affordance (Figma `hasTooltip`). */
	hasTooltip?: boolean;
	/** Tooltip copy shown on the help icon. */
	tooltip?: string;
	/** Show the trailing resize handle (Figma `hasResizer`). */
	hasResizer?: boolean;
	/** Optional live resizer node; overrides the decorative handle when set. */
	resizer?: ReactNode;
	/**
	 * Show the leading drag handle (Figma `hasDragHandle`).
	 * Visual only — column reordering is handled via Manage Columns / DnD wrappers.
	 */
	hasDragHandle?: boolean;
	/** Extra content rendered beside the label (e.g. Aria column children). */
	children?: ReactNode;
}

const alignClassName: Record<TableHeaderCellAlign, string> = {
	left: "justify-start",
	center: "justify-center",
	right: "justify-end",
};

const stateClassName: Record<TableHeaderCellState, string> = {
	default: "bg-tertiary",
	hover: "bg-quaternary",
	focused: "bg-tertiary z-1 ring-2 ring-focus-ring ring-inset",
};

/**
 * Presentational table header cell matching Figma Table/HeaderCell (3047:841).
 * Live React Aria tables use `CustomTable.Head` for the same chrome; use this
 * shell (or registered `HeaderCell`) outside Aria / in demos.
 */
export function TableHeaderCell({
	align = TableHeaderCellAlign.Left,
	sortable = TableHeaderCellSortable.None,
	state = TableHeaderCellState.Default,
	label,
	overline = "Overline",
	hasOverline = false,
	hasTooltip = false,
	tooltip,
	hasResizer = false,
	resizer,
	hasDragHandle = false,
	children,
	className,
	...props
}: TableHeaderCellProps) {
	const showSortIcon = sortable !== TableHeaderCellSortable.None;
	const tooltipTitle = tooltip ?? "More info";

	const helpIcon = hasTooltip ? (
		tooltip ? (
			<Tooltip title={tooltipTitle} placement="top">
				<TooltipTrigger className="cursor-pointer text-fg-quaternary transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover">
					<HelpCircle className="size-4 shrink-0" aria-hidden />
				</TooltipTrigger>
			</Tooltip>
		) : (
			<span className="text-fg-quaternary">
				<HelpCircle className="size-4 shrink-0" aria-hidden />
			</span>
		)
	) : null;

	const sortIcon =
		sortable === TableHeaderCellSortable.Asc ? (
			<ArrowNarrowUp
				className="size-4.5 shrink-0 text-fg-quaternary"
				aria-hidden
			/>
		) : sortable === TableHeaderCellSortable.Desc ? (
			<ArrowNarrowDown
				className="size-4.5 shrink-0 text-fg-quaternary"
				aria-hidden
			/>
		) : sortable === TableHeaderCellSortable.Unsorted ? (
			<ChevronSelectorVertical
				className="size-4.5 shrink-0 text-fg-quaternary"
				aria-hidden
			/>
		) : null;

	const resizerNode =
		resizer ??
		(hasResizer ? (
			<div className="relative h-6 w-1 shrink-0 overflow-hidden" aria-hidden>
				<div className="absolute top-0 left-0 h-4 w-0.5 rounded-sm bg-fg-quaternary" />
			</div>
		) : null);

	return (
		<div
			{...props}
			data-align={align}
			data-sortable={sortable}
			data-state={state}
			data-slot="table-header-cell"
			className={cx(
				// Figma Table/HeaderCell — h-40, px-24 py-4, gap-xs
				// No overflow on the root: absolute resizer uses translateX(50%) past the edge.
				// Label group clips horizontally so bold descenders stay unclipped vertically.
				// No fixed w-[200px]: live tables size from the Aria th (max 215); demos
				// pass width via className when they need the Figma 200px shell.
				"relative flex h-10 min-w-0 w-auto max-w-[215px] items-center gap-1 overflow-visible px-6 py-1",
				alignClassName[align],
				stateClassName[state],
				showSortIcon && "cursor-pointer",
				className,
			)}
		>
			{hasDragHandle ? (
				<span className="flex shrink-0 items-center text-fg-quaternary">
					<DotsGrid className="size-4.5" aria-hidden />
				</span>
			) : null}

			<div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 overflow-x-clip overflow-y-visible whitespace-nowrap">
				{hasOverline ? (
					<span className="text-xs font-semibold leading-4 text-quaternary">
						{overline}
					</span>
				) : null}
				{/* leading-5 + x-clip ellipsis: avoid vertical ink clip on bold labels.
				    Sort hugs the title (gap-1); help/resizer stay trailing on the cell. */}
				<div className="flex min-w-0 items-center gap-1 text-sm leading-5 font-bold text-primary">
					{label ? (
						<span className="min-w-0 overflow-x-clip overflow-y-visible text-ellipsis whitespace-nowrap">
							{label}
						</span>
					) : null}
					{children}
					{sortIcon}
				</div>
			</div>

			{helpIcon}
			{resizerNode}
		</div>
	);
}
